import { useState, useEffect } from "react";
import "./newUser.css";
import { registerStaff } from "../../redux/apiCalls";
import { useDispatch } from "react-redux";
import { publicRequest } from "../../requestMethods";
import Select from "react-select";
import { useSelector } from "react-redux";
import { PersonAdd, Save, Business, Group } from "@material-ui/icons";

export default function NewUser() {
    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin : null
    );
    const currentUser = useSelector((state) => state.user.currentUser);

    const [inputs, setInputs] = useState({
        outlet: superadmin ? undefined : currentUser._id,
    });
    const [usersData, setUsersData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const dispatch = useDispatch();

    const handleChange = (e) => {
        setInputs((prev) => {
            return { ...prev, [e.target.name]: e.target.value };
        });
        // Clear messages when user starts typing
        if (errorMessage) setErrorMessage("");
        if (successMessage) setSuccessMessage("");
    };

    const handleClick = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        const user = { ...inputs };

        try {
            await registerStaff({ user });
            setSuccessMessage("Staff created successfully!");
            window.location.reload();
        } catch (err) {
            console.log(err);
            setErrorMessage("Failed to create staff. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputUserId = (e) => {
        setInputs((prev) => {
            return { ...prev, outlet: e.value };
        });
    };

    const getAdmins = async () => {
        const res = await publicRequest.get("/users/admin", {
            params: {
                type: ["outright"],
            },
        });
        setUsersData(res.data);
    };

    useEffect(() => {
        const fetchData = async () => {
            await getAdmins(dispatch, []);
        };
        fetchData();
    }, [dispatch]);

    return (
        <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden new-staff-page">
            {/* Luxury Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-500"></div>
            </div>

            <div className="relative z-10 max-w-5xl mx-auto p-6 page-container">
                {/* Premium Glass Container */}
                <div className="bg-gray-200 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-10 relative overflow-hidden form-shell">
                    {/* Luxury Header Overlay */}
                    <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-amber-600/10 blur-xl"></div>

                    {/* Ultra-Premium Header */}
                    <div className="relative z-10 mb-12 header-block">
                        <div className="text-center mb-8">
                            <div className="relative inline-block group">
                                {/* Animated Rings */}
                                <div className="absolute inset-0 rounded-full">
                                    <div className="absolute inset-0 rounded-full border-2 border-purple-500/30 animate-ping"></div>
                                    <div
                                        className="absolute inset-2 rounded-full border-2 border-amber-500/40 animate-ping"
                                        style={{ animationDelay: "0.5s" }}
                                    ></div>
                                    <div
                                        className="absolute inset-4 rounded-full border-2 border-blue-500/50 animate-ping"
                                        style={{ animationDelay: "1s" }}
                                    ></div>
                                </div>

                                {/* Central Icon */}
                                <div className="relative w-24 h-24 bg-gradient-to-br from-purple-500 via-amber-600 to-blue-500 rounded-full flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-700 shadow-2xl">
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-full"></div>
                                    <Group className="text-white text-4xl relative z-10" />
                                </div>
                            </div>

                            {/* Luxury Status Bar */}
                            <div className="flex items-center justify-center gap-6 mt-8 status-row">
                                <div className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border border-purple-200/50 shadow-lg status-pill">
                                    <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse shadow-lg shadow-purple-500/50"></div>
                                    <span className="text-sm font-bold text-purple-700 tracking-wide">
                                        STAFF MANAGEMENT
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/50 shadow-lg status-pill">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shadow-lg shadow-blue-500/50"></div>
                                    <span className="text-sm font-bold tracking-wide">
                                        SECURED CONNECTION
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Premium Success Message */}
                    {successMessage && (
                        <div className="mb-8 relative group animate-in slide-in-from-top duration-500">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-green-400/20 rounded-2xl blur-xl"></div>
                            <div className="relative bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200/50 rounded-2xl p-6 backdrop-blur-sm">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-emerald-500 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center">
                                            <span className="text-white text-xl">
                                                ✓
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-bold text-emerald-800 mb-1">
                                            Success!
                                        </h4>
                                        <p className="text-emerald-700 font-medium">
                                            {successMessage}
                                        </p>
                                    </div>
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Premium Error Message */}
                    {errorMessage && (
                        <div className="mb-8 relative group animate-in slide-in-from-top duration-500">
                            <div className="absolute inset-0 bg-gradient-to-r from-red-400/20 to-rose-400/20 rounded-2xl blur-xl"></div>
                            <div className="relative bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200/50 rounded-2xl p-6 backdrop-blur-sm">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-red-500 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center">
                                            <span className="text-white text-xl">
                                                !
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-bold text-red-800 mb-1">
                                            Error
                                        </h4>
                                        <p className="text-red-700 font-medium">
                                            {errorMessage}
                                        </p>
                                    </div>
                                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleClick} className="space-y-10">
                        {/* Premium Staff Configuration Section */}
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-indigo-400/10 rounded-3xl blur-xl"></div>
                            <div className="relative bg-black text-white backdrop-blur-sm rounded-3xl p-8 border-2 border-purple-200/30 shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:border-purple-300/50 section-card">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="relative">
                                        <div className="absolute inset-0 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                                            <span className="text-white text-xl">
                                                🏢
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-black text-white mb-2">
                                            Staff Configuration
                                        </h3>
                                        <p className="text-purple-200 font-medium">
                                            Organization and role assignment
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse"></div>
                                        <span className="text-purple-200 text-sm font-medium">
                                            Required
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {superadmin && (
                                        <div className="group space-y-3 md:col-span-2">
                                            <label className="text-sm flex items-center font-bold text-white tracking-wide group-hover:text-purple-300 transition-colors duration-200">
                                                Outlet Assignment
                                            </label>
                                            <div className="relative">
                                                <Select
                                                    options={usersData.map(
                                                        (user, i) => ({
                                                            label: `${user.name}`,
                                                            value: user._id,
                                                        })
                                                    )}
                                                    onChange={handleInputUserId}
                                                    placeholder="Select Outlet"
                                                    styles={{
                                                        control: (
                                                            provided
                                                        ) => ({
                                                            ...provided,
                                                            backgroundColor: "#fff",
                                                            border: "2px solid rgba(148, 163, 184, 0.5)",
                                                            borderRadius: "16px",
                                                            padding: "8px 12px",
                                                            fontSize: "14px",
                                                            fontWeight: "500",
                                                            color: "#000",
                                                            boxShadow: "none",
                                                            "&:hover": {
                                                                backgroundColor: "#fff",
                                                                borderColor: "rgba(147, 51, 234, 0.7)",
                                                                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                                                            },
                                                        }),
                                                        placeholder: (
                                                            provided
                                                        ) => ({
                                                            ...provided,
                                                            color: "#94a3b8",
                                                        }),
                                                        singleValue: (
                                                            provided
                                                        ) => ({
                                                            ...provided,
                                                            color: "#000",
                                                            fontWeight: "500",
                                                        }),
                                                        option: (
                                                            provided,
                                                            state
                                                        ) => ({
                                                            ...provided,
                                                            backgroundColor:
                                                                state.isSelected
                                                                    ? "#147236"
                                                                    : state.isFocused
                                                                    ? "#f3f4f6"
                                                                    : "#fff",
                                                            color: state.isSelected
                                                                ? "#fff"
                                                                : "#000",
                                                            fontWeight: "500",
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "#f3f4f6",
                                                                color: "#000",
                                                            },
                                                        }),
                                                        menu: (provided) => ({
                                                            ...provided,
                                                            borderRadius: "12px",
                                                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                                                            border: "2px solid rgba(148, 163, 184, 0.2)",
                                                            backgroundColor: "#fff"
                                                        }),
                                                    }}
                                                />
                                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="group space-y-3 md:col-span-2">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-purple-300 transition-colors duration-200">
                                            Staff Type{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <Select
                                                options={[
                                                    {
                                                        label: "HQ",
                                                        value: "HQ",
                                                    },
                                                    {
                                                        label: "Salesperson",
                                                        value: "salesperson",
                                                    },
                                                ]}
                                                onChange={(e) => {
                                                    setInputs((prev) => ({
                                                        ...prev,
                                                        type: e.value,
                                                    }));
                                                }}
                                                placeholder="Select Staff Type"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        backgroundColor: "#fff",
                                                        border: "2px solid rgba(148, 163, 184, 0.5)",
                                                        borderRadius: "16px",
                                                        padding: "8px 12px",
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                        color: "#000",
                                                        boxShadow: "none",
                                                        "&:hover": {
                                                            backgroundColor: "#fff",
                                                            borderColor: "rgba(147, 51, 234, 0.7)",
                                                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                                                        },
                                                    }),
                                                    placeholder: (
                                                        provided
                                                    ) => ({
                                                        ...provided,
                                                        color: "#94a3b8",
                                                    }),
                                                    singleValue: (
                                                        provided
                                                    ) => ({
                                                        ...provided,
                                                        color: "#000",
                                                        fontWeight: "500",
                                                    }),
                                                    option: (
                                                        provided,
                                                        state
                                                    ) => ({
                                                        ...provided,
                                                        backgroundColor:
                                                            state.isSelected
                                                                ? "#147236"
                                                                : state.isFocused
                                                                ? "#f3f4f6"
                                                                : "#fff",
                                                        color: state.isSelected
                                                            ? "#fff"
                                                            : "#000",
                                                        fontWeight: "500",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#f3f4f6",
                                                            color: "#000",
                                                        },
                                                    }),
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        borderRadius: "12px",
                                                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                                                        border: "2px solid rgba(148, 163, 184, 0.2)",
                                                        backgroundColor: "#fff"
                                                    }),
                                                }}
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Premium Staff Information Section */}
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-indigo-400/10 rounded-3xl blur-xl"></div>
                            <div className="relative bg-black text-white backdrop-blur-sm rounded-3xl p-8 border-2 border-blue-200/30 shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:border-blue-300/50 section-card">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="relative">
                                        <div className="absolute inset-0 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                                            <span className="text-white text-xl">
                                                👥
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-black text-white mb-2">
                                            Staff Information
                                        </h3>
                                        <p className="text-blue-200 font-medium">
                                            Personal details and authentication
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                                        <span className="text-blue-200 text-sm font-medium">
                                            Required
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Full Name{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="name"
                                                type="text"
                                                placeholder="Enter full name"
                                                value={inputs.name || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Login ID{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="email"
                                                type="text"
                                                placeholder="Enter username"
                                                value={inputs.email || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Phone Number{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="phoneNumber"
                                                type="tel"
                                                placeholder="Enter phone number"
                                                value={inputs.phoneNumber || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Password{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="password"
                                                type="password"
                                                placeholder="Enter password"
                                                value={inputs.password || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ultra-Premium Submit Section */}
                        <div className="relative pt-16">
                            <div className="flex justify-center">
                                <div className="relative group">
                                    <div className="absolute inset-0 rounded-3xl">
                                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 to-blue-600/20 animate-ping"></div>
                                        <div
                                            className="absolute inset-2 rounded-3xl bg-gradient-to-r from-blue-500/30 to-indigo-500/30 animate-ping"
                                            style={{ animationDelay: "0.5s" }}
                                        ></div>
                                        <div
                                            className="absolute inset-4 rounded-3xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 animate-ping"
                                            style={{ animationDelay: "1s" }}
                                        ></div>
                                    </div>

                                    {/* Main Glow Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-blue-600 to-indigo-500 rounded-3xl blur-2xl opacity-75 group-hover:opacity-100 transition-opacity duration-700"></div>

                                    {/* Ultra-Premium Button */}
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`relative px-16 py-6 bg-black from-slate-900 via-blue-900 to-slate-900 hover:from-blue-900 hover:via-purple-900 hover:to-blue-900 text-white font-black text-xl rounded-3xl shadow-2xl transition-all duration-700 transform hover:scale-110 hover:shadow-3xl flex items-center gap-6 border-2 border-white/20 backdrop-blur-xl primary-action-btn ${
                                            isLoading
                                                ? "opacity-75 cursor-not-allowed scale-95"
                                                : "hover:border-purple-400/50"
                                        }`}
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="relative">
                                                    <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    <div
                                                        className="absolute inset-0 w-8 h-8 border-4 border-transparent border-t-purple-400 rounded-full animate-spin"
                                                        style={{
                                                            animationDirection:
                                                                "reverse",
                                                            animationDuration:
                                                                "0.8s",
                                                        }}
                                                    ></div>
                                                </div>
                                                <span className="text-xl tracking-wider">
                                                    Creating Staff...
                                                </span>
                                                <div className="flex gap-1">
                                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                                    <div
                                                        className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"
                                                        style={{
                                                            animationDelay:
                                                                "0.2s",
                                                        }}
                                                    ></div>
                                                    <div
                                                        className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"
                                                        style={{
                                                            animationDelay:
                                                                "0.4s",
                                                        }}
                                                    ></div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-xl tracking-wider font-black bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                                                    Create New Staff
                                                </span>
                                                <div className="flex gap-2">
                                                    <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse shadow-lg shadow-blue-400/50"></div>
                                                    <div
                                                        className="w-3 h-3 bg-purple-400 rounded-full animate-pulse shadow-lg shadow-purple-400/50"
                                                        style={{
                                                            animationDelay:
                                                                "0.3s",
                                                        }}
                                                    ></div>
                                                    <div
                                                        className="w-3 h-3 bg-indigo-400 rounded-full animate-pulse shadow-lg shadow-indigo-400/50"
                                                        style={{
                                                            animationDelay:
                                                                "0.6s",
                                                        }}
                                                    ></div>
                                                </div>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
